import { Pets as PawIcon } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  Collapse,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useState } from "react";
import { reviews } from "../data/reviews.ts";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

// Styled components
const StyledReviewsSection = styled(Box)<{ $isDarkMode: boolean }>(({ theme, $isDarkMode }) => ({
  padding: theme.spacing(8, 0),
  backgroundColor: $isDarkMode ? theme.palette.background.default : '#f8f4f7',
}));

const StyledReviewsContainer = styled(Container)(({ theme }) => ({
  maxWidth: 'lg',
}));

const StyledReviewsHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(6),
}));

const StyledReviewsTitle = styled(Typography)<{ component?: React.ElementType }>(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
}));

const StyledReviewsSubtitle = styled(Typography)(({ theme }) => ({
  maxWidth: '800px',
  margin: '0 auto',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(4),
}));

const StyledReviewCard = styled(Card)<{ $isDarkMode: boolean }>(({ theme, $isDarkMode }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: $isDarkMode ? theme.palette.background.paper : 'white',
  borderRadius: theme.spacing(1.5),
  boxShadow: theme.shadows[4],
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const StyledReviewCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
}));

const StyledReviewHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const StyledReviewAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  width: 40,
  height: 40,
  marginRight: theme.spacing(2),
}));

const StyledReviewName = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.primary.main,
}));

const StyledReviewDate = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.disabled,
  alignSelf: 'flex-end',
  marginTop: 'auto',
}));

const StyledReviewMessage = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  flexGrow: 1,
  fontStyle: 'italic',
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
  height: '4.8em', // Fixed height for 3 lines
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
}));

const StyledExpandedReviewMessage = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  flexGrow: 1,
  fontStyle: 'italic',
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
}));

const StyledExpandButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  marginLeft: 'auto',
  color: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
}));

const StyledReviewActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: 'auto',
}));

export const ReviewsSection: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [expandedReviews, setExpandedReviews] = useState<Set<number>>(new Set());

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("nl-NL", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const handleToggleRow = (rowIndex: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowIndex)) {
      newExpanded.delete(rowIndex);
    } else {
      newExpanded.add(rowIndex);
    }
    setExpandedRows(newExpanded);
  };

  const handleToggleReview = (reviewIndex: number) => {
    const newExpanded = new Set(expandedReviews);
    if (newExpanded.has(reviewIndex)) {
      newExpanded.delete(reviewIndex);
    } else {
      newExpanded.add(reviewIndex);
    }
    setExpandedReviews(newExpanded);
  };

  // Group reviews into rows of 2
  const reviewRows: typeof reviews[] = [];
  for (let i = 0; i < reviews.length; i += 2) {
    reviewRows.push(reviews.slice(i, i + 2));
  }

  return (
    <StyledReviewsSection
      id="reviews"
      component="section"
      aria-labelledby="reviews-heading"
      $isDarkMode={isDarkMode}
    >
      <StyledReviewsContainer>
        <StyledReviewsHeader>
          <StyledReviewsTitle
            variant="h4"
            component="h2"
            id="reviews-heading"
          >
            <PawIcon />
            Klantbeoordelingen
          </StyledReviewsTitle>
          <StyledReviewsSubtitle variant="body1">
            Ontdek wat onze tevreden klanten over Pippaya zeggen
          </StyledReviewsSubtitle>
        </StyledReviewsHeader>

        {reviewRows.map((row, rowIndex) => {
          const isRowExpanded = expandedRows.has(rowIndex);
          const hasLongReview = row.some(review => review.message.length > 200);
          
          return (
            <Grid container spacing={3} key={rowIndex} sx={{ mb: 3 }}>
              {row.map((review, reviewIndex) => {
                const globalIndex = rowIndex * 2 + reviewIndex;
                const isLongReview = review.message.length > 200;
                const isExpanded = isMobile ? expandedReviews.has(globalIndex) : isRowExpanded;
                
                return (
                  <Grid item xs={12} md={6} key={globalIndex}>
                    <StyledReviewCard $isDarkMode={isDarkMode}>
                      <StyledReviewCardContent>
                        {/* Positive Review Indicator */}
                        <StyledReviewHeader>
                          <StyledReviewAvatar>
                            <PawIcon />
                          </StyledReviewAvatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <StyledReviewName variant="h6">
                              {review.name}
                            </StyledReviewName>
                          </Box>
                        </StyledReviewHeader>

                        {/* Review Message */}
                        {isExpanded ? (
                          <StyledExpandedReviewMessage variant="body1">
                            "{review.message}"
                          </StyledExpandedReviewMessage>
                        ) : (
                          <StyledReviewMessage variant="body1">
                            "{review.message}"
                          </StyledReviewMessage>
                        )}

                        {/* Actions and Date */}
                        <StyledReviewActions>
                          {review.date && (
                            <StyledReviewDate variant="caption">
                              {formatDate(review.date)}
                            </StyledReviewDate>
                          )}
                          {isLongReview && (
                            <StyledExpandButton
                              onClick={() => isMobile ? handleToggleReview(globalIndex) : handleToggleRow(rowIndex)}
                              aria-label={isExpanded ? "Verkort review" : "Lees volledige review"}
                            >
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </StyledExpandButton>
                          )}
                        </StyledReviewActions>
                      </StyledReviewCardContent>
                    </StyledReviewCard>
                  </Grid>
                );
              })}
            </Grid>
          );
        })}
      </StyledReviewsContainer>
    </StyledReviewsSection>
  );
};
